import { API_ENDPOINTS } from "@/constants/api";
import { http } from "@/lib/http-client";
import type {
  DelegationsOverview,
  DelegationValidationReport,
} from "@/types/delegations";

/**
 * «الإيفادات الرسمية» dataset API — workbook in, published record table out.
 *
 * ⚠️ LIKE `/api/obs-datasets`, THESE ROUTES ARE DELIBERATELY ABSENT FROM THE
 * BACKEND'S `ENVELOPE_PREFIXES`. The response envelope discards the body on any
 * status >= 400, and the entire point of a 422 or a 409 here is the report saying
 * WHY. So responses arrive RAW — no `unwrapData` — a 422/409 is a normal return
 * value rather than a throw, and a Strapi error body has to be told apart from a
 * real report rather than rendering as an empty, reassuring result.
 */

interface StrapiErrorBody {
  error?: { status?: number; name?: string; message?: string };
}

function throwIfStrapiError(payload: unknown): void {
  const err = (payload as StrapiErrorBody | null)?.error;
  if (err && (err.message || err.name)) {
    throw new Error(err.message || err.name || "فشل الطلب");
  }
}

function asReport(payload: unknown): DelegationValidationReport {
  throwIfStrapiError(payload);
  const report = payload as DelegationValidationReport | null;
  if (!report || !Array.isArray(report.errors)) {
    throw new Error("استجابة غير متوقعة من الخادم");
  }
  return report;
}

export const delegationsService = {
  async overview(): Promise<DelegationsOverview> {
    const response = await http.get(API_ENDPOINTS.DELEGATIONS.LIST);
    throwIfStrapiError(response);
    return response as DelegationsOverview;
  },

  /** Pure dry run — nothing is stored and nothing is replaced. */
  async validate(slug: string, file: File): Promise<DelegationValidationReport> {
    const body = new FormData();
    body.append("file", file);
    return asReport(
      await http.upload("POST", API_ENDPOINTS.DELEGATIONS.VALIDATE(slug), body),
    );
  },

  /**
   * Publish. `force` overrides WARNINGS only — never errors, which the backend
   * refuses regardless of what is sent.
   */
  async upload(
    slug: string,
    file: File,
    force = false,
  ): Promise<DelegationValidationReport> {
    const body = new FormData();
    body.append("file", file);
    return asReport(
      await http.upload("POST", API_ENDPOINTS.DELEGATIONS.UPLOAD(slug, force), body),
    );
  },

  /** Rollback. Needs no file and cannot fail on parse — that is the whole point. */
  async activate(slug: string, version: number): Promise<void> {
    const response = await http.post(
      API_ENDPOINTS.DELEGATIONS.ACTIVATE(slug, version),
      {},
    );
    throwIfStrapiError(response);
  },
};
